import { useCallback, useEffect, useMemo, useState } from 'react';
import { useChord } from '@app/hooks/useChord';
import { useHaptics } from '@app/hooks/useHaptics';
import { useAppStore } from '@app/store/useAppStore';
import { getTuningById } from '@theory/tunings';
import { INTERVAL_BY_LABEL } from '@theory/intervals';
import { transposeNote } from '@util/musicTheory';
import { getVariantChipIds } from '@util/chordVariants';
import { ChordDiagram } from './ChordDiagram';
import { ChordVariantChips } from './ChordVariantChips';
import { IntervalBadge } from './IntervalBadge';
import styles from './ChordCard.module.css';

const FUNCTION_COLOURS: Record<string, string> = {
  Tonic: 'var(--colour-tonic)',
  Subdominant: 'var(--colour-subdominant)',
  Dominant: 'var(--colour-dominant)',
  Leading: 'var(--colour-leading)',
};

interface ChordCardProps {
  chordId: string;
  degree?: string;
  romanNumeral?: string;
  chordFunction?: string;
}

export function ChordCard({ chordId: initialId, degree, romanNumeral, chordFunction }: ChordCardProps) {
  const [chordId, setChordId] = useState(initialId);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setChordId(initialId);
    setPage(0);
  }, [initialId]);

  const chord = useChord(chordId);
  // Variant chips are anchored to the BASE diatonic chord (initialId), never the
  // active selection — otherwise picking F#sus2/F#sus4 on an F#m card would
  // rebuild the menu from sus2/sus4's (major-oriented) variants, dropping the
  // minor siblings and surfacing a bare "F#" major chip (a one-way exit to major).
  const variantChipIds = useMemo(() => getVariantChipIds(initialId), [initialId]);
  const haptics = useHaptics();
  const tuningId = useAppStore((s) => s.selectedTuningId);
  const isLeftHanded = useAppStore((s) => s.isLeftHanded);
  const tuning = getTuningById(tuningId);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) haptics.light();
      return !prev;
    });
  }, [haptics]);

  if (!chord) return null;

  const fingering = chord.fingerings[page];
  const fnColour = chordFunction ? FUNCTION_COLOURS[chordFunction] : null;

  return (
    <article className={[styles.card, open && styles.open].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.header}
        onClick={toggle}
        aria-expanded={open}
        aria-label={`${chord.displayName}${chordFunction ? ', ' + chordFunction : ''}`}
      >
        {romanNumeral && (
          <span className={styles.romanBadge} style={fnColour ? { color: fnColour } : undefined}>
            {romanNumeral}
          </span>
        )}
        {!romanNumeral && degree && <span className={styles.degree}>{degree}</span>}
        <span className={styles.headerText}>
          <span className={styles.name}>{chord.displayName}</span>
          {chordFunction && <span className={styles.function}>{chordFunction}</span>}
        </span>
        <span className={styles.chevron} aria-hidden>
          ›
        </span>
      </button>

      {open && (
        <div className={styles.inner}>
          {chord.unavailable ? (
            <p className={styles.unavailable}>Chords unavailable for {tuning.name}.</p>
          ) : (
            <>
              {chord.fingerings.length > 1 && (
                <div className={styles.pager}>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    aria-label="Previous fingering"
                  >
                    ‹
                  </button>
                  <span>
                    {page + 1} / {chord.fingerings.length}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) => Math.min(chord.fingerings.length - 1, p + 1))
                    }
                    disabled={page >= chord.fingerings.length - 1}
                    aria-label="Next fingering"
                  >
                    ›
                  </button>
                </div>
              )}
              {fingering && (
                <ChordDiagram
                  fingering={fingering}
                  chordRoot={chord.root}
                  tuning={tuning}
                  isLeftHanded={isLeftHanded}
                />
              )}
              <ul className={styles.intervals}>
                {chord.intervals.map((iv) => {
                  const semitones = INTERVAL_BY_LABEL[iv]?.semitones ?? 0;
                  const noteName = transposeNote(chord.root, semitones);
                  return (
                    <li key={iv}>
                      <IntervalBadge intervalLabel={iv} noteName={noteName} />
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          <ChordVariantChips variantIds={variantChipIds} selectedId={chordId} onSelect={setChordId} />
        </div>
      )}
    </article>
  );
}
